import React, { useCallback, useMemo, useState } from "react";

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { saveAs } from "file-saver";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useHistory, useParams } from "react-router-dom";
import { toast } from "sonner";

import FreecopyCodeLinkDialog from "./FreecopyCodeLinkDialog";
import { freecopyBatchColumns } from "./tableParams/freecopyBatchColumns";
import { freecopyColumns } from "./tableParams/freecopyColumns";
import { freecopyUniqueCodeColumns } from "./tableParams/freecopyUniqueCodeColumns";
import OfferAPI from "../../api/OfferAPI";
import useAbility from "../../hooks/useAbility";
import CappedPage from "../common/CappedPage";
import CreateButton from "../common/CreateButton";
import DataTable from "../dataTable/DataTable";
import {
  freecopyCodeEditPath,
  freecopyEditPath,
  PROJECTS_ROUTE,
} from "../routes/routes";
import Breadcrumbs from "../utils/Breadcrumbs";

import type { JSX } from "react";

import type { FreecopyBatchItem } from "../../api/OfferAPI.types";
import type { AppState } from "../../reducers";
import type { FreecopyRow } from "./tableParams/freecopyColumns.types";
import type { FreecopyUniqueCodeRow } from "./tableParams/freecopyUniqueCodeColumns.types";

const EMPTY_BATCH_LIST: FreecopyBatchItem[] = [];

function FreecopyList(): JSX.Element {
  const { t } = useTranslation("editor-freecopy");
  const { projectSlug } = useParams<{ projectSlug: string }>();
  const routerHistory = useHistory();
  const queryClient = useQueryClient();

  const project = useSelector(
    (state: AppState) => state.architectureReducer.project,
  );
  const orgaOptions = useSelector(
    (state: AppState) =>
      state.fetchReducer.orga_config.data?.organization_options,
  );

  const { canAccess } = useAbility();
  const { project: canAccessProject } = canAccess(orgaOptions);
  const canWrite = canAccessProject.freeCopiesWrite;

  const getFreecopiesQuery = OfferAPI.generateQuery({
    fn: OfferAPI.getFreecopies,
    params: [{ projectSlug }],
  });

  const getFreecopiesBatchQuery = OfferAPI.generateQuery({
    fn: OfferAPI.getFreecopiesBatch,
    params: [{ projectSlug }],
  });

  const getFreecopiesUniqueCodeQuery = OfferAPI.generateQuery({
    fn: OfferAPI.getFreecopiesUniqueCode,
    params: [{ projectSlug }],
  });

  const getOfferPlansQuery = OfferAPI.generateQuery({
    fn: OfferAPI.getOfferPlans,
    params: [{ projectSlug }],
  });

  const {
    data: freecopiesData,
    isLoading: freecopiesLoading,
    isError: freecopiesError,
    refetch: refetchFreecopies,
  } = useQuery(getFreecopiesQuery);
  const {
    data: freecopiesBatchData,
    isLoading: freecopiesBatchLoading,
    isError: freecopiesBatchError,
    refetch: refetchFreecopiesBatch,
  } = useQuery(getFreecopiesBatchQuery);
  const {
    data: freecopiesUniqueCodeData,
    isLoading: freecopiesCodeLoading,
    isError: freecopiesCodeError,
    refetch: refetchFreecopiesCode,
  } = useQuery(getFreecopiesUniqueCodeQuery);
  const { data: offerPlansData } = useQuery(getOfferPlansQuery);

  const freecopyRowList = useMemo(
    (): FreecopyRow[] =>
      (freecopiesData?.data ?? []).map((freecopy) => {
        const offerPlan = offerPlansData?.data.find(
          ({ offer }) => offer.uuid === freecopy.offer_plan.offer_uuid,
        );
        const activationDate =
          freecopy.status === "activable"
            ? freecopy.activation_limit_date
            : (freecopy.subscription?.begin_date ?? "");

        return {
          ...freecopy,
          offer_name: offerPlan?.offer.label ?? "",
          activationDate,
        };
      }),
    [freecopiesData?.data, offerPlansData?.data],
  );

  const uniqueCodeRowList = useMemo(
    (): FreecopyUniqueCodeRow[] =>
      (freecopiesUniqueCodeData?.data ?? []).map((uniqueCode) => {
        const offerPlan = offerPlansData?.data.find(
          ({ uuid }) => uuid === uniqueCode.offer_plan_uuid,
        );

        return {
          ...uniqueCode,
          status: uniqueCode.status ? "enabled" : "disabled",
          offer_name: offerPlan?.offer.label ?? "",
        };
      }),
    [freecopiesUniqueCodeData?.data, offerPlansData?.data],
  );

  const [linkedCode, setLinkedCode] = useState<string | null>(null);

  const { mutate: resendEmail } = useMutation({
    mutationFn: async (uuid: string) =>
      OfferAPI.updateFreecopy({
        uuid,
        data: { resend_email: true },
        projectSlug,
      }),
    onSuccess: () => {
      toast.success(t("editor-notif:saveSuccess"));
    },
    onError: () => {
      toast.error(t("editor-notif:saveError"));
    },
  });

  const { mutate: deleteFreecopy } = useMutation({
    mutationFn: async (uuid: string) =>
      OfferAPI.deleteFreecopy({ uuid, projectSlug }),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: getFreecopiesQuery.queryKey,
      });
      toast.success(t("deleteComplete"));
    },
    onError: () => {
      toast.error(t("editor-notif:saveError"));
    },
  });

  const { mutate: exportFreecopyCode } = useMutation({
    mutationFn: async (uuid: string) =>
      OfferAPI.exportFreecopyUniqueCode({ uuid, projectSlug }),
    onSuccess: (response) => {
      const csv = new File([response], "unique_code.csv", {
        type: "text/csv",
      });
      saveAs(csv);
    },
    onError: () => {
      toast.error(t("editor-notif:saveError"));
    },
  });

  const handleEdit = useCallback(
    (freecopy: FreecopyRow): void =>
      routerHistory.push(freecopyEditPath(projectSlug, freecopy.uuid)),
    [routerHistory, projectSlug],
  );

  const handleEditCode = useCallback(
    (uniqueCode: FreecopyUniqueCodeRow): void =>
      routerHistory.push(freecopyCodeEditPath(projectSlug, uniqueCode.uuid)),
    [routerHistory, projectSlug],
  );

  const columnList = useMemo(
    () =>
      freecopyColumns({
        t,
        canWrite,
        onResendEmail: (freecopy) => resendEmail(freecopy.uuid),
        onEdit: handleEdit,
        onDelete: (freecopy) => deleteFreecopy(freecopy.uuid),
      }),
    [t, canWrite, resendEmail, handleEdit, deleteFreecopy],
  );

  const batchColumnList = useMemo(() => freecopyBatchColumns({ t }), [t]);

  const uniqueCodeColumnList = useMemo(
    () =>
      freecopyUniqueCodeColumns({
        t,
        canWrite,
        onLink: (uniqueCode) => setLinkedCode(uniqueCode.code),
        onEdit: handleEditCode,
        onExport: (uniqueCode) => exportFreecopyCode(uniqueCode.uuid),
      }),
    [t, canWrite, handleEditCode, exportFreecopyCode],
  );

  const breadcrumbList = [
    {
      name: t("editor-menu:offerManagement"),
      url: "/freecopy",
    },
    {
      name: t("sectionTitleSimple"),
    },
  ];

  const createButton = canWrite ? (
    <CreateButton
      href={`${PROJECTS_ROUTE}/${projectSlug}/freecopy/new/`}
      label={t("createFreecopy")}
    />
  ) : undefined;

  const createUniqueCodeButton = canWrite ? (
    <CreateButton
      href={`${PROJECTS_ROUTE}/${projectSlug}/freecopy-code/new/`}
      label={t("createUniqueCode")}
    />
  ) : undefined;

  return (
    <CappedPage contentClassName="pt-7.5 pb-15">
      <div data-testid="FreecopyList">
        <Breadcrumbs items={breadcrumbList} />
        <div className="flex flex-col gap-6">
          <DataTable
            columns={columnList}
            data={freecopyRowList}
            defaultSorting={{ id: "updated_at", desc: true }}
            headerActions={createButton}
            isError={freecopiesError}
            isLoading={freecopiesLoading}
            onRetry={refetchFreecopies}
            title={t("sectionTitle")}
            urlSync={{ prefix: "freecopies" }}
          />
          <DataTable
            columns={batchColumnList}
            data={freecopiesBatchData?.data ?? EMPTY_BATCH_LIST}
            defaultSorting={{ id: "updated_at", desc: true }}
            isError={freecopiesBatchError}
            isLoading={freecopiesBatchLoading}
            onRetry={refetchFreecopiesBatch}
            title={t("batchSectionTitle")}
            urlSync={{ prefix: "freecopiesBatch" }}
          />
          <DataTable
            columns={uniqueCodeColumnList}
            data={uniqueCodeRowList}
            defaultSorting={{ id: "updated_at", desc: true }}
            headerActions={createUniqueCodeButton}
            isError={freecopiesCodeError}
            isLoading={freecopiesCodeLoading}
            onRetry={refetchFreecopiesCode}
            storageKey="freecopyUniqueCodes"
            title={t("uniqueCodeSectionTitle")}
            urlSync={{ prefix: "uniqueCodes" }}
          />
        </div>
      </div>
      <FreecopyCodeLinkDialog
        onClose={() => setLinkedCode(null)}
        link={
          linkedCode === null
            ? null
            : `${project?.base_url}/offers/freecopy?copy_token=${linkedCode}`
        }
      />
    </CappedPage>
  );
}

export default FreecopyList;
